import React, { useMemo } from "react";
import useAsyncRetry from "react-use/lib/useAsyncRetry";
import { InputCustomChangeEventHandler } from "../../types";
import useT from "../../hooks/useT";
import InputSelectCustom, {
  ReactSelectOption,
  InputSelectCustomProps
} from "../InputSelectCustom";
import { loadTitles } from "../../utils/titles";

// @TODO when Omit is available as a built-in TS helper:
// interface InputTitleProps extends Omit<InputSelectCustomProps, "options" | "onChange"> {}
export interface InputTitleProps
  extends Pick<
    InputSelectCustomProps,
    Exclude<keyof InputSelectCustomProps, "options" | "onChange">
  > {
  /**
   * If the input is optional, you can show a "prefer not to say" option which will
   * set the value to an empty string.
   *
   * @default false
   */
  showEmptyOption?: boolean;
  /** Event handler to publish changes to the controlling parent component. */
  onChange: InputCustomChangeEventHandler;
}

const HAS_NO_TRANSLATION = "—";

export default function InputTitle({
  searchable = false,
  clearable = true,
  showEmptyOption = false,
  ...props
}: InputTitleProps) {
  const TITLE_OPTIONS = useAsyncRetry(async () => await loadTitles(), []);

  const { t, i18n } = useT();
  const lng = props.lang || i18n.language;

  const options = useMemo(() => {
    const _options: ReactSelectOption[] = [];

    if (!TITLE_OPTIONS) {
      throw new Error(
        "Options for honorificPrefixes not set in the maison's configuration"
      );
    }

    if (showEmptyOption) {
      _options.push({
        label: "",
        value: null
      });
    }

    const translatedOptions =
      TITLE_OPTIONS.value && !TITLE_OPTIONS.loading
        ? TITLE_OPTIONS.value
            .map(({ label, value }) => ({
              label: String(
                t(label, {
                  lng,
                  defaultValue: HAS_NO_TRANSLATION
                })
              ).trim(),
              value
            }))
            // Remove any options whose label has no translation,
            // or the translated label is empty
            .filter(({ label }) => !!label && label !== HAS_NO_TRANSLATION)
        : [];

    if (!!translatedOptions) {
      return _options.concat(translatedOptions);
    }

    return _options;
  }, [lng, clearable, TITLE_OPTIONS]);

  return (
    <InputSelectCustom
      {...props}
      options={options}
      disabled={props.disabled}
      clearable={clearable}
      searchable={searchable}
    />
  );
}
